import { isPast } from 'date-fns/isPast';
import { isThisMonth } from 'date-fns/isThisMonth';
import { isToday } from 'date-fns/isToday';

import { getZonedDate } from '@eika-infrastruktur/date';
import { formatCurrencyValue } from '@eika/currency-input';

import { Account, EFaktura, PaymentLimits } from '../../../generated-types';
import { format } from '../../../hooks/useTranslation';
import {
    doesAmountExceedAccountBalanceAccountBalance,
    doesAmountExceedPaymentLimit,
    PaymentLimitValidationError,
} from '../../../utils/validation';
import { getCombinedEFakturaAmount } from './getCombinedEFakturaAmount';

export type BatchEFakturaErrorMessages = {
    chooseDebitAccount: string;
    noCoverage: string;
    exceedsMaximumAmount: string;
    exceedsMonthlyMaximumAmount: string;
};

const getCoverageStatusForEFakturaBatchRequest = (
    eFakturas: EFaktura[],
    targetDebitAccount: Account | undefined,
    paymentLimits: PaymentLimits | undefined,
): PaymentLimitValidationError | 'no-coverage' | 'valid' => {
    if (!targetDebitAccount) return 'no-coverage';
    if (eFakturas.length === 0) return 'valid';

    const onlyEFakturasBeingPaidToday = eFakturas.filter(
        (eFaktura) => isToday(eFaktura.paymentDate) || isPast(eFaktura.paymentDate),
    );
    const futureEFakturas = eFakturas.filter(
        (eFaktura) => isPast(eFaktura.paymentDate) || isThisMonth(eFaktura.paymentDate),
    );

    const combinedAmountToday = getCombinedEFakturaAmount(onlyEFakturasBeingPaidToday);
    const combinedFutureAmount = getCombinedEFakturaAmount(futureEFakturas);
    const paymentLimitError = eFakturas
        .map((eFaktura) =>
            doesAmountExceedPaymentLimit(
                Math.max(eFaktura.amount, eFaktura.minimumAmount ?? 0),
                getZonedDate(eFaktura.paymentDate),
                paymentLimits,
            ),
        )
        .find(Boolean);

    if (paymentLimitError) return paymentLimitError;

    if (
        combinedFutureAmount >
        (paymentLimits?.maxMonthlyTransactionAmount ?? 0) - (paymentLimits?.accumulatedMonthlyTransactionAmount ?? 0)
    )
        return 'amountExceedsMonthlyMaximum';

    if (doesAmountExceedAccountBalanceAccountBalance(targetDebitAccount, combinedAmountToday)) return 'no-coverage';

    return 'valid';
};

const validate = (
    eFakturas: EFaktura[],
    targetDebitAccount: Account | undefined,
    paymentLimits: PaymentLimits | undefined,
    errorMessages: BatchEFakturaErrorMessages,
): string | undefined => {
    if (!targetDebitAccount) {
        return errorMessages.chooseDebitAccount;
    }

    const coverageStatus = getCoverageStatusForEFakturaBatchRequest(eFakturas, targetDebitAccount, paymentLimits);
    switch (coverageStatus) {
        case 'amountExceedsMaximum':
            return format(
                errorMessages.exceedsMaximumAmount,
                formatCurrencyValue(paymentLimits?.maxTransactionAmount ?? 0),
            );
        case 'amountExceedsMonthlyMaximum':
            return format(
                errorMessages.exceedsMonthlyMaximumAmount,
                formatCurrencyValue(paymentLimits?.maxMonthlyTransactionAmount ?? 0),
            );
        case 'no-coverage':
            return errorMessages.noCoverage;
        case 'valid':
        default:
            return undefined;
    }
};

export default validate;
